import { useMemo } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ArrowRight, ImagePlus, Plus, Trash2 } from "lucide-react";
import IconButton from "../../../components/Buttons/IconButton";
import Button from "../../../components/Buttons/Button";
import TextField from "../../../components/Form/TextField";
import { useTranslationApp } from "../../../lib/i18next";
import {
  IMAGE,
  TEMPLATES_INLINE_REPLACEMENTS_ADD,
  TEMPLATES_INLINE_REPLACEMENTS_HINT,
  TEMPLATES_INLINE_REPLACEMENTS_PHRASE,
  TEMPLATES_INLINE_REPLACEMENTS_REMOVE,
  TEMPLATES_INLINE_REPLACEMENTS_TITLE,
} from "../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER, PRIMARY_COLOR_TEXT } from "../../../utils/colors";
import {
  createInlineReplacementId,
  listStoredInlineReplacements,
} from "../utils/textInlineReplacements";

const optionalRules = { required: false };
const ROW_SIZE = 40;
const ARROW_SIZE = 16;
const DELETE_SIZE = 36;

const phraseFieldName = (id) => `phrase_${id}`;

const columnHeaderSx = {
  color: "text.secondary",
  fontWeight: 500,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
};

const AssetThumb = ({ url, disabled, onClick }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      position: "relative",
      width: ROW_SIZE,
      height: ROW_SIZE,
      minWidth: ROW_SIZE,
      minHeight: ROW_SIZE,
      flexShrink: 0,
      boxSizing: "border-box",
      borderRadius: "10px",
      border: url
        ? `1px solid ${INPUT_COLOR_BORDER}`
        : `1.5px dashed ${INPUT_COLOR_BORDER}`,
      bgcolor: url ? "fields.background" : "transparent",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
      color: "text.secondary",
      transition: "border-color 0.15s ease, color 0.15s ease",
      "&:hover": disabled
        ? undefined
        : {
            color: "text.primary",
            borderColor: "text.secondary",
          },
    }}
  >
    {url ? (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />
    ) : (
      <ImagePlus size={18} />
    )}
  </Box>
);

const TextInlineReplacementsFields = ({
  replacements = [],
  urls = {},
  disabled,
  onChangeReplacements,
  onSelectAsset,
}) => {
  const { t } = useTranslationApp();
  const slots = listStoredInlineReplacements(replacements);
  const formValues = useMemo(
    () =>
      Object.fromEntries(
        listStoredInlineReplacements(replacements).map((slot) => [
          phraseFieldName(slot.id),
          slot.phrase || "",
        ])
      ),
    [replacements]
  );
  const { control } = useForm({ values: formValues });

  const commit = (next) => {
    onChangeReplacements?.(next);
  };

  const onAdd = () => {
    commit([...slots, { id: createInlineReplacementId(), phrase: "" }]);
  };

  const onPatch = (id, patch) => {
    commit(slots.map((slot) => (slot.id === id ? { ...slot, ...patch } : slot)));
  };

  const onRemove = (id) => {
    commit(slots.filter((slot) => slot.id !== id));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t(TEMPLATES_INLINE_REPLACEMENTS_TITLE)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", lineHeight: 1.4 }}
        >
          {t(TEMPLATES_INLINE_REPLACEMENTS_HINT)}
        </Typography>
      </Box>
      {slots.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            mt: "16px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ ...columnHeaderSx, flex: 1 }}>
              {t(TEMPLATES_INLINE_REPLACEMENTS_PHRASE)}
            </Typography>
            <Box sx={{ width: ARROW_SIZE, flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{ ...columnHeaderSx, width: ROW_SIZE, flexShrink: 0 }}
            >
              {t(IMAGE)}
            </Typography>
            <Box sx={{ width: DELETE_SIZE, flexShrink: 0 }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {slots.map((slot) => {
        const url = slot.assetRef ? urls[slot.assetRef] : "";
        return (
          <Box
            key={slot.id}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                control={control}
                errors={{}}
                name={phraseFieldName(slot.id)}
                variant="filled"
                placeholder={t(TEMPLATES_INLINE_REPLACEMENTS_PHRASE)}
                fullWidth
                disabled={disabled}
                rules={optionalRules}
                actionsChange={(value) => onPatch(slot.id, { phrase: value })}
                sx={{
                  "& .MuiFilledInput-root": {
                    height: ROW_SIZE,
                    minHeight: ROW_SIZE,
                    boxSizing: "border-box",
                    borderRadius: "10px",
                    "&:before, &:after": { display: "none" },
                  },
                  "& .MuiFilledInput-input": {
                    padding: "0 12px",
                    height: ROW_SIZE,
                    boxSizing: "border-box",
                  },
                  "& .MuiFormHelperText-root": {
                    display: "none",
                    m: 0,
                    height: 0,
                    minHeight: 0,
                    overflow: "hidden",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                width: ARROW_SIZE,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <ArrowRight size={ARROW_SIZE} />
            </Box>
            <AssetThumb
              url={url}
              disabled={disabled}
              onClick={() => onSelectAsset?.(slot.id)}
            />
            <IconButton
              disabled={disabled}
              edge={false}
              tooltipText={TEMPLATES_INLINE_REPLACEMENTS_REMOVE}
              onClick={() => onRemove(slot.id)}
              sx={{
                width: DELETE_SIZE,
                height: DELETE_SIZE,
                p: 0.5,
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </Box>
        );
      })}
          </Box>
        </Box>
      ) : null}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mt: "4px" }}>
        <Button
          color="secondary"
          iconOnly
          disabled={disabled}
          ariaLabel={t(TEMPLATES_INLINE_REPLACEMENTS_ADD)}
          tooltip={t(TEMPLATES_INLINE_REPLACEMENTS_ADD)}
          startIcon={<Plus size={20} color={PRIMARY_COLOR_TEXT} />}
          onClick={onAdd}
          sx={{
            borderRadius: "50%",
            color: `${PRIMARY_COLOR_TEXT} !important`,
            "& svg": {
              color: `${PRIMARY_COLOR_TEXT} !important`,
              stroke: PRIMARY_COLOR_TEXT,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default TextInlineReplacementsFields;
